// @flow strict

import type {ReactRef} from 'src/types/react';

import * as React from 'react';


type DragContext = {
  pointerId: number,
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
  deltaX: number,
  deltaY: number,
  target: EventTarget,
  timeStamp: number,
};

export function usePointerDrag({
  onDown,
  onMove,
  onUp,
  onCancel,
  onLost,
  onDone,
  draggingClassName,
  validateDownEvent,
}: {
  onDown?: (DragContext) => mixed,
  onMove?: (DragContext) => mixed,
  onUp?: (DragContext) => mixed,
  onCancel?: (DragContext) => mixed,
  onLost?: (DragContext) => mixed,
  onDone?: (DragContext) => mixed,
  draggingClassName?: string,
  validateDownEvent?: (SyntheticEvent<>) => mixed,
}): [
  {
    onPointerDown: (event: SyntheticPointerEvent<>) => void,
    onPointerMove: (event: SyntheticPointerEvent<>) => void,
    onPointerUp: (event: SyntheticPointerEvent<>) => void,
    onPointerCancel: (event: SyntheticPointerEvent<>) => void,
    onLostPointerCapture: (event: SyntheticPointerEvent<>) => void,
    onContextMenu: (event: SyntheticEvent<>) => void,
  },
  ReactRef<DragContext>,
] {
  const definedOnCancel = onCancel ?? onUp;
  const definedOnLost = onLost ?? onUp;

  const dragContextRef: ReactRef<DragContext> = React.useRef<?DragContext>();

  const handlePointerMove = (event: SyntheticPointerEvent<>, handler) => {
    const dragContext = dragContextRef.current;
    if (!dragContext || event.pointerId !== dragContext.pointerId) {
      return;
    }

    dragContext.deltaX = event.clientX - dragContext.currentX;
    dragContext.deltaY = event.clientY - dragContext.currentY;

    dragContext.currentX = event.clientX;
    dragContext.currentY = event.clientY;

    handler && handler(dragContext);
  };

  const handlePointerDone = (event: SyntheticPointerEvent<>, handler) => {
    handlePointerMove(event, handler);
    handlePointerFinally(event);
  };

  const handlePointerFinally = (_event: SyntheticEvent<>) => {
    const {body} = document;
    body && draggingClassName && body.classList.remove(draggingClassName);
    onDone && dragContextRef.current && onDone(dragContextRef.current);
    dragContextRef.current = null;
  };

  return [
    {
      onPointerDown(event: SyntheticPointerEvent<>) {
        if (
          !event.isPrimary ||
          (validateDownEvent && !validateDownEvent(event))
        ) {
          return;
        }

        event.preventDefault();

        const {body} = document;
        body && draggingClassName && body.classList.add(draggingClassName);

        // $FlowIssue number vs string
        event.currentTarget.setPointerCapture(event.pointerId);

        const dragContext = (dragContextRef.current = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          currentX: event.clientX,
          currentY: event.clientY,
          timeStamp: event.timeStamp,
          target: event.target,
          deltaX: 0,
          deltaY: 0,
        });

        onDown && onDown(dragContext);
      },

      onPointerMove(event: SyntheticPointerEvent<>) {
        handlePointerMove(event, onMove);
      },

      onPointerUp(event: SyntheticPointerEvent<>) {
        event.preventDefault();

        // NOTE (kyle): if the pointerup event fires within 200ms, we call it a
        // click.
        const dragContext = dragContextRef.current;
        if (
          dragContext &&
          event.pointerId === dragContext.pointerId &&
          event.timeStamp - dragContext.timeStamp < 200
        ) {
          const {target} = dragContext;
          target instanceof HTMLElement && target.click();
        }

        handlePointerDone(event, onUp);
      },

      onPointerCancel(event: SyntheticPointerEvent<>) {
        handlePointerDone(event, definedOnCancel);
      },

      onLostPointerCapture(event: SyntheticPointerEvent<>) {
        handlePointerDone(event, definedOnLost);
      },

      onContextMenu(event: SyntheticEvent<>) {
        if (dragContextRef.current) {
          definedOnLost && definedOnLost(dragContextRef.current);
          handlePointerFinally(event);
        }
      },
    },
    dragContextRef,
  ];
}
